import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Box, Boxes, Smartphone, Truck, CreditCard, Mail, ShoppingCart, Sparkles, ArrowLeftRight, Hourglass, Undo2, Landmark, Flag, ClipboardCheck, ClipboardList, Hand, Layers, ScanLine, LayoutDashboard, LogOut, MapPin, Menu, Package, PackageCheck, PackagePlus, Printer, RefreshCw, Search, Settings, ShieldCheck, Store, Tags, TreePine, Shirt, Contact, Percent, Gift, Users, Warehouse } from 'lucide-react';
import { useAuth } from '../auth';
import { get } from '../api';
import { LogoMark, LogoWordmark } from './Logo';

type Item = { to: string; label: string; icon: typeof Boxes; cap: string | null };
const groups: { title: string; items: Item[] }[] = [
  { title: '', items: [{ to: '/', label: 'Overblik', icon: LayoutDashboard, cap: null }] },
  {
    title: 'Ordrer',
    items: [
      { to: '/orders', label: 'Ordrer', icon: Package, cap: 'orders.view' },
      { to: '/shops', label: 'Shops', icon: Store, cap: 'shops.view' },
      { to: '/feed', label: '3x-feed', icon: RefreshCw, cap: 'shops.view' },
    ],
  },
  {
    title: 'Webshop',
    items: [
      { to: '/products', label: 'Produkter', icon: Shirt, cap: 'catalogue.view' },
      { to: '/sampakker', label: 'Sampakker', icon: PackagePlus, cap: 'catalogue.view' },
      { to: '/categories', label: 'Kategorier', icon: Tags, cap: 'catalogue.view' },
      { to: '/customers', label: 'Kunder', icon: Contact, cap: 'customers.view' },
      { to: '/discounts', label: 'Rabatter', icon: Percent, cap: 'shops.view' },
      { to: '/handout', label: 'Udlevering', icon: Gift, cap: 'handout.edit' },
      { to: '/gaveuniverset', label: 'Gaveuniverset', icon: Sparkles, cap: 'gu.view' },
    ],
  },
  {
    title: 'Lagergulv',
    items: [
      { to: '/batches', label: 'Pakkebatches', icon: Layers, cap: 'shops.view' },
      { to: '/pick', label: 'Pluk', icon: Hand, cap: 'floor.pick' },
      { to: '/pack', label: 'Pak', icon: PackageCheck, cap: 'floor.pack' },
      { to: '/shipping', label: 'Fragt', icon: Truck, cap: 'orders.view' },
      { to: '/returns', label: 'Returer', icon: Undo2, cap: 'orders.view' },
      { to: '/scan', label: 'Scan og print', icon: ScanLine, cap: 'floor.scan_print' },
      { to: '/printing', label: 'Print', icon: Printer, cap: 'printing.print' },
    ],
  },
  {
    title: 'Lager',
    items: [
      { to: '/stock', label: 'Beholdning', icon: Boxes, cap: 'stock.view' },
      { to: '/purchasing', label: 'Indkøb', icon: ShoppingCart, cap: 'purchasing.view' },
      { to: '/transfers', label: 'Overførsler', icon: ArrowLeftRight, cap: 'stock.view' },
      { to: '/backorders', label: 'Restordrer', icon: Hourglass, cap: 'orders.view' },
      { to: '/locations', label: 'Lokationer', icon: MapPin, cap: 'stock.view' },
      { to: '/counts', label: 'Optælling', icon: ClipboardCheck, cap: 'stock.view' },
      { to: '/packaging', label: 'Emballage', icon: Box, cap: 'stock.view' },
      { to: '/warehouses', label: 'Lagre & Navision', icon: Warehouse, cap: 'stock.view' },
    ],
  },
  {
    title: 'Christmas packing',
    items: [
      { to: '/xmas', label: 'Julepakning', icon: TreePine, cap: 'xmas.view' },
      { to: '/xmas/scan', label: 'Scan stregkode', icon: ScanLine, cap: 'xmas.view' },
    ],
  },
  {
    title: 'System',
    items: [
      { to: '/users', label: 'Brugere', icon: Users, cap: 'users.manage' },
      { to: '/roles', label: 'Roller', icon: ShieldCheck, cap: 'users.manage' },
      { to: '/finance', label: 'Økonomi', icon: Landmark, cap: 'finance.view' },
      { to: '/cutover', label: 'Skift', icon: Flag, cap: 'orders.view' },
      { to: '/x3-import', label: 'Flyt shops fra 3x', icon: RefreshCw, cap: 'shops.view' },
      { to: '/payments', label: 'Betaling', icon: CreditCard, cap: 'finance.settings' },
      { to: '/mail', label: 'Mails', icon: Mail, cap: 'orders.view' },
      { to: '/audit', label: 'Log', icon: ClipboardList, cap: 'audit.view' },
      { to: '/settings', label: 'Indstillinger', icon: Settings, cap: 'settings.manage' },
    ],
  },
];

function initials(name: string | undefined) {
  return (name ?? '?').split(/\s+/).map((p) => p[0]?.toUpperCase() ?? '').slice(0, 2).join('');
}

export function Shell() {
  const { me, can, logout } = useAuth();
  const goTo = useNavigate();
  // A hand-out scanner has nothing to do in the back office: straight to the handheld.
  useEffect(() => {
    if (me && can('handout.scan') && !can('orders.view') && !can('handout.edit')) void goTo({ to: '/m/udlevering', replace: true });
    // Christmas packing only: the front page has nothing for them, so a login lands at the scan station.
    else if (me && can('xmas.view') && !can('orders.view') && window.location.pathname === '/') void goTo({ to: '/xmas/scan', replace: true });
  }, [me]);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('nav.collapsed') === '1'; } catch { return false; } });
  const [q, setQ] = useState('');
  const toggle = () => { const v = !collapsed; setCollapsed(v); try { localStorage.setItem('nav.collapsed', v ? '1' : '0'); } catch { /* ignore */ } };
  const attention = useQuery({ queryKey: ['orders', 'attention'], queryFn: () => get<{ total: number }>('/orders?status=needs_check&perPage=1'), enabled: can('orders.view'), refetchInterval: 60_000 });
  return (
    <div className="flex min-h-screen">
      <aside className={`sticky top-0 flex h-screen flex-col border-r border-line bg-white transition-all ${collapsed ? 'w-[68px]' : 'w-60'}`}>
        <div className={`flex h-16 items-center ${collapsed ? 'justify-center' : 'px-5'}`}>
          <Link to="/">{collapsed ? <LogoMark size={30} /> : <LogoWordmark />}</Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {groups.map((g) => {
            const items = g.items.filter((n) => !n.cap || can(n.cap));
            if (!items.length) return null;
            return (
              <div key={g.title || 'top'}>
                {g.title && !collapsed && <div className="nav-group">{g.title}</div>}
                {g.title && collapsed && <div className="my-3 border-t border-line" />}
                {items.map((n) => (
                  <Link key={n.to} to={n.to} className={`nav-item ${collapsed ? 'justify-center px-0' : ''}`} activeProps={{ className: 'nav-item active' }} activeOptions={{ exact: n.to === '/' }} title={collapsed ? n.label : undefined}>
                    <n.icon size={18} strokeWidth={1.8} />
                    {!collapsed && <span>{n.label}</span>}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-white/90 px-6 backdrop-blur">
          <button className="rounded-lg p-2 text-muted hover:bg-canvas hover:text-ink" onClick={toggle} aria-label="Menu">
            <Menu size={20} />
          </button>
          <form
            className="relative hidden w-80 md:block"
            onSubmit={(e) => { e.preventDefault(); if (q.trim()) { void navigate({ to: '/orders', search: { q: q.trim() } as never }); } }}
          >
            <Search size={16} className="pointer-events-none absolute left-3 top-2.5 text-muted" />
            <input className="input pl-9" placeholder="Søg ordre, kunde, SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
          </form>
          <div className="ml-auto flex items-center gap-2">
            {can('floor.pick') && (
              <Link to="/m" className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-muted hover:bg-canvas hover:text-primary sm:flex" title="Håndterminal til pluk">
                <Smartphone size={18} /> Håndterminal
              </Link>
            )}
            {can('orders.view') && (
              <Link to="/orders" search={{ status: 'needs_check' } as never} className="relative rounded-lg p-2 text-muted hover:bg-canvas hover:text-ink" title="Ordrer der skal tjekkes">
                <AlertTriangle size={20} />
                {(attention.data?.total ?? 0) > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-danger px-1.5 text-[10px] font-semibold leading-4 text-white">{attention.data!.total > 999 ? '999+' : attention.data!.total}</span>}
              </Link>
            )}
            <Link to="/account" className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-canvas" title="Min konto">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">{initials(me?.name)}</span>
              <span className="hidden text-sm font-medium md:block">{me?.name}</span>
            </Link>
            <button className="rounded-lg p-2 text-muted hover:bg-canvas hover:text-ink" title="Log ud" onClick={async () => { await logout(); await navigate({ to: '/login' }); }}>
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
